import {format, differenceInCalendarDays, differenceInHours} from 'date-fns';
import {Flag, Badge, getInitials, ColorSchemeType, BadgeColorScheme} from 'platform/components';
import {Center, HStack, Text, VStack, getSize, Hide, Show} from 'platform/foundation';
import {css, useTheme} from 'styled-components';

import i18n from '@dms/i18n';

import {RequiredTestIdProps} from 'shared/web';

import {CALENDAR_CONSTANTS} from '../constants/calendarConstants';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import type {CollisionLayout} from '../utils/detectEventCollisions';
import {getColorPathByScheme} from '../utils/getColorPathByScheme';
import {getEventCollisionLayout} from '../utils/getEventCollisionLayout';
import {getRentalEventBarLayout} from '../utils/getRentalEventBarLayout';
import {getThemeToken} from '../utils/getThemeToken';
import {repeatingGradient} from '../utils/repeatingGradient';

interface RentalEventProps extends RequiredTestIdProps {
  originalEvent: RentalResourceCalendarEvent;
  eventIndex: number;
  monthStart: Date;
  monthEnd: Date;
  daysInMonthCount: number;
  collisionLayout: CollisionLayout;
  hoveredEvent: RentalResourceCalendarEvent | null;
  referenceRef: (element: HTMLElement | null) => void;
  onEventClick?: (event: RentalResourceCalendarEvent) => void;
  onEventMouseEnter: (event: RentalResourceCalendarEvent) => void;
  onEventMouseLeave: () => void;
}

export function RentalEvent(props: RentalEventProps) {
  const theme = useTheme();

  const eventLayout = getRentalEventBarLayout({
    event: props.originalEvent,
    monthStart: props.monthStart,
    monthEnd: props.monthEnd,
    daysInMonthCount: props.daysInMonthCount,
    dayColumnWidth: CALENDAR_CONSTANTS.DAY_COLUMN_WIDTH / 8, // Quarter column width
    useQuarterPositioning: true,
  });

  const colorPath = getColorPathByScheme(
    props.originalEvent.rentalOrder?.stateFlagColor || 'neutral'
  );
  const EVENT_COLOR_HEX = getThemeToken(theme, `colors.${colorPath}`);

  const customerName =
    props.originalEvent.rentalOrder?.customerName ||
    i18n.t('page.rentalOrder.labels.notPlannedYet');

  const collisionInfo = getEventCollisionLayout(props.originalEvent, props.collisionLayout);

  const eventHeight = Math.floor(CALENDAR_CONSTANTS.ROW_HEIGHT * 0.8); // 80% of row height
  const eventTop =
    collisionInfo.rowIndex * CALENDAR_CONSTANTS.ROW_HEIGHT +
    Math.floor(CALENDAR_CONSTANTS.ROW_HEIGHT * 0.1); // 10% padding from top

  return (
    <div
      ref={props.hoveredEvent?.id === props.originalEvent.id ? props.referenceRef : undefined}
      css={css`
        position: absolute;
        top: ${eventTop}px;
        left: ${eventLayout.left}px;
        width: ${eventLayout.width}px;
        height: ${eventHeight}px;
        background-color: transparent;
        background-image: ${repeatingGradient(EVENT_COLOR_HEX)};
        border: 2px solid ${EVENT_COLOR_HEX};
        border-radius: ${theme.radii.small};
        box-shadow: ${({theme}) => theme.shadows[theme.components.Card.elevation]};
        cursor: pointer;
        display: flex;
        align-items: center;
        padding: ${getSize(1)} ${getSize(2)};
        min-width: 2px;
        overflow: hidden;
        transition: opacity 0.2s ease;
        z-index: ${props.eventIndex + 1};
        &:hover {
          box-shadow: ${({theme}) => theme.shadows.elevation_2};
        }
      `}
      onClick={() => props.onEventClick?.(props.originalEvent)}
      onMouseEnter={() => props.onEventMouseEnter(props.originalEvent)}
      onMouseLeave={props.onEventMouseLeave}
    >
      <Show
        when={differenceInCalendarDays(props.originalEvent.end, props.originalEvent.start) <= 1}
      >
        <Center width="100%" height="100%">
          <VStack width="100%" align="center" justify="center">
            <Text size="xSmall" alternative color="primary" noWrap>
              {getInitials(customerName)}
            </Text>
            {differenceInHours(props.originalEvent.end, props.originalEvent.start) > 18 ? (
              <Text size="xxSmall" color="primary" noWrap>
                {`${format(props.originalEvent.start, 'H:mm')} - ${format(props.originalEvent.end, 'H:mm')}`}
              </Text>
            ) : null}
          </VStack>
        </Center>
      </Show>
      <Hide
        when={differenceInCalendarDays(props.originalEvent.end, props.originalEvent.start) <= 1}
      >
        <VStack width="100%" align="center" justify="center">
          <Text size="xSmall" alternative color="primary" noWrap>
            <HStack align="center" spacing={1}>
              {customerName}
              <Show
                when={
                  differenceInCalendarDays(props.originalEvent.end, props.originalEvent.start) >=
                  2.5
                }
              >
                <Flag
                  size="small"
                  label={props.originalEvent.rentalOrder?.stateLabel || ''}
                  colorScheme={
                    (props.originalEvent.rentalOrder?.stateFlagColor as ColorSchemeType) ||
                    'neutral'
                  }
                />
              </Show>
              <Show
                when={
                  differenceInCalendarDays(props.originalEvent.end, props.originalEvent.start) >= 3
                }
              >
                <Badge
                  colorScheme={
                    (props.originalEvent.rentalOrder?.stateFlagColor as BadgeColorScheme) ||
                    'neutral'
                  }
                  size="small"
                  variant="subtle"
                  value={props.originalEvent.rentalOrder?.rentalLength ?? 1}
                />
              </Show>
            </HStack>
          </Text>
          <Text size="xxSmall" color="primary" noWrap>
            {`${format(props.originalEvent.start, 'd.M H:mm')} - ${format(props.originalEvent.end, 'd.M H:mm')}`}
          </Text>
        </VStack>
      </Hide>
    </div>
  );
}
